/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, type PropsWithChildren } from 'react';
import { Alert, Box, Snackbar, type AlertColor } from '@mui/material';
import type { ApiError } from '#/store/slices/api/types/error';

interface SnackbarState {
  message: string;
  type: AlertColor;
}

interface SnackbarContextType {
  showSnackbar: (message: string, type: AlertColor) => void;
  showApiErrorSnackbar: (fallbackMessage: string) => (e: ApiError) => void
}

const SnackbarContext = createContext<SnackbarContextType>({
  showSnackbar: () => null,
  showApiErrorSnackbar: () => () => null,
});

export const SnackbarProvider = ({ children }: PropsWithChildren) => {
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);

  const showSnackbar = (message: string, type: AlertColor) => {
    setSnackbar({ message, type });
  };

  const showApiErrorSnackbar = (fallbackMessage: string ) => (e: ApiError) => {
    if (!e.data?.details) {
      showSnackbar(e.data?.message ?? fallbackMessage, 'error');
      return;
    }

    showSnackbar(`${e.data.message}${e.data.details.map((d) => `\n - ${d.message}`)}`, 'error');
  };

  const handleClose = () => {
    setSnackbar(null);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, showApiErrorSnackbar }}>
      {children}
      <Snackbar
        open={!!snackbar}
        onClose={handleClose}
        autoHideDuration={3000}
      >
        {snackbar ? (
          <Alert severity={snackbar.type} sx={{ whiteSpace: 'pre-line' }}>
            {snackbar.message}
          </Alert>
        ) : <Box />}
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => useContext(SnackbarContext);